import type { ApolloClient, OnDataOptions } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import * as Throttle from 'promise-parallel-throttle';
import type {
  ClusterRoute,
  GetCachedExactClusterRoutesQueryResult,
  GetClusterRouteQuery,
  GetClusterRouteQueryResult,
  GetClusterRouteQueryVariables,
  GetClusterRoutesQueryResult,
  GetClusterRoutesQueryVariables,
  RouteOverrideUpdatedSubscription,
} from '../../graphql';
import {
  GetCachedExactClusterRoutesDocument,
  GetClusterRouteDocument,
  GetClusterRoutesDocument,
  useAllNoGoZonesQuery,
  useAllRouteOverridesQuery,
  useRouteOverrideUpdatedSubscription,
} from '../../graphql';
import {
  exhaustiveCheck,
  filteredMap,
  isDefined,
  NONE,
  useAsRef,
} from '../../lib/util';
import type {
  DistanceContextProps,
  Distances,
  LocationPair,
  OverrideParams,
  RouteData,
  RouteParams,
} from './context';
import { DistanceContext } from './context';
import { MultiKey } from '../core/MultiKey';
import { stringToNumOpt } from '../core/util';
import { Button, Modal, ModalBody, ModalFooter, Text } from '../ui';
import {
  getAllResolvedLocationPairs,
  getMissingDistances,
  parseRoute,
  resolveEffectiveLocation,
  routeParamsToString,
} from './distance-utils';
import { getStoredDistances, setStoredDistance } from './store';
import { useCurrentUser } from '../../state/zustand';
import { useNotify } from '../notifications/ToastNotifications';
import type { ScenarioLocation } from '../../lib/planningData';
import { DistanceMap } from './distanceMap';
import { partition, chunk as chunkArray } from 'lodash';
import { zeroDistance } from './constant';
import { useIsMobileView } from '../hooks/useMediaQuery';
import type { ClusterId, LocationId } from '@seaber/common/core/entries';
import { DefaultMap } from '@seaber/common/lib/collections';

interface DistanceContextProviderProps {
  children: React.ReactNode;
}

type OverrideResponse = {
  params: OverrideParams;
  response: RouteData;
};
type DistanceResponse = { params: RouteParams; response: RouteData };
type DistanceError = { params: RouteParams; error: string };

interface DistancesState {
  distances: Distances;
  queue: RouteParams[];
  responses: DistanceResponse[];
  errors: DistanceError[];
  loading: boolean;
  unapprovedQueue: LocationPair[];
  draft: number | null;
  noGoZones: string;
  locations: Map<LocationId, ScenarioLocation>;
  overrides: MultiKey<RouteData, OverrideParams>;
}

type DistancesAction =
  | { type: 'DISTANCES_RESET'; distances: Distances }
  | { type: 'DISTANCES_ADDED'; distances: DistanceResponse[] }
  | { type: 'OVERRIDES_ADDED'; distances: OverrideResponse[] }
  | { type: 'QUEUE_PROCESSED' }
  | { type: 'ADDED_TO_QUEUE'; locationPairs: LocationPair[] }
  | { type: 'RESPONSE_RECEIVED'; response: DistanceResponse }
  | { type: 'RESPONSES_RECEIVED'; responses: DistanceResponse[] }
  | { type: 'ERROR_RECEIVED'; response: DistanceError }
  | { type: 'LOADING_STARTED' }
  | { type: 'LOADING_ENDED' }
  | { type: 'UNAPPROVED_QUEUE_RESET'; locationPairs: LocationPair[] }
  | { type: 'SET_DRAFT'; draft: number | null }
  | { type: 'SET_NOGOZONES'; noGoZones: string }
  | { type: 'SET_LOCATIONS'; locations: Map<LocationId, ScenarioLocation> }
  | {
      type: 'SET_OVERRIDES';
      overrides: MultiKey<RouteData, OverrideParams>;
    }
  | { type: 'FETCHING_APPROVED' };
type DistancesDispatch = React.Dispatch<DistancesAction>;

const CACHED_DISTANCE_CHUNK_SIZE = 1000;

export const initialDistanceMap = new DistanceMap(
  new MultiKey(5),
  new Map(),
  null,
  'null',
  new MultiKey(3),
);
const initialState = {
  distances: initialDistanceMap,
  queue: [],
  responses: [],
  errors: [],
  loading: false,
  unapprovedQueue: [],
  draft: null,
  noGoZones: 'null',
  locations: initialDistanceMap.locationsById,
  overrides: initialDistanceMap.overrides,
};
function getInitialState(): DistancesState {
  return initialState;
}
export const locationPairToRouteParams = (
  locations: Map<LocationId, ScenarioLocation>,
) =>
  function locationPairToRouteParams(pair: LocationPair): RouteParams | null {
    const [fromLocationId, toLocationId, avoidsChannels, draft, usedNoGoZones] =
      pair;
    const fromClusterId = resolveEffectiveLocation(
      locations,
      fromLocationId,
    )?.clusterId;
    const toClusterId = resolveEffectiveLocation(
      locations,
      toLocationId,
    )?.clusterId;
    if (!fromClusterId || !toClusterId) {
      return null;
    }
    return [fromClusterId, toClusterId, avoidsChannels, draft, usedNoGoZones];
  };

const distancesReducer = (
  state: DistancesState,
  action: DistancesAction,
): DistancesState => {
  switch (action.type) {
    case 'DISTANCES_RESET': {
      return {
        ...state,
        distances: action.distances,
      };
    }
    case 'DISTANCES_ADDED': {
      if (action.distances.length === 0) {
        return state;
      }
      const newDistances = new DistanceMap(
        state.distances.map,
        state.locations,
        state.draft,
        state.noGoZones,
        state.distances.overrides,
      );
      const addedPairs = new Set(
        action.distances.map((d) => routeParamsToString(d.params)),
      );

      // Remove added distances from queue, errors and responses
      const newQueue = state.queue.filter(
        (queuedPair) => !addedPairs.has(routeParamsToString(queuedPair)),
      );
      const newErrors = state.errors.filter(
        (err) => !addedPairs.has(routeParamsToString(err.params)),
      );
      const newResponses = state.responses.filter(
        (response) => !addedPairs.has(routeParamsToString(response.params)),
      );

      action.distances.forEach(({ response, params }) => {
        newDistances.set(response, ...params);
      });

      window.distancesLoaded = newQueue.length === 0;

      return {
        ...state,
        distances: newDistances,
        errors: newErrors,
        queue: newQueue,
        responses: newResponses,
      };
    }
    case 'OVERRIDES_ADDED': {
      if (action.distances.length === 0) {
        return state;
      }
      const newDistances = new DistanceMap(
        state.distances.map,
        state.locations,
        state.draft,
        state.noGoZones,
        state.distances.overrides,
      );

      action.distances.forEach(({ response, params }) => {
        const [fromId, toId, avoidsChannels] = params;
        newDistances.overrides.set(response, fromId, toId, avoidsChannels);
      });

      return {
        ...state,
        distances: newDistances,
      };
    }
    case 'QUEUE_PROCESSED': {
      if (state.responses.length === 0 && state.errors.length === 0) {
        return state;
      }
      const newDistances = new DistanceMap(
        state.distances.map,
        state.locations,
        state.draft,
        state.noGoZones,
        state.distances.overrides,
      );

      state.responses.forEach(({ params, response }) => {
        newDistances.set(response, ...params);
      });

      // We set 0 distance for errored locations so that actions that required
      // the whole distance matrix to be available can still be used
      state.errors.forEach(({ params }) => {
        newDistances.set(zeroDistance, ...params);
      });

      return {
        ...state,
        distances: newDistances,
        loading: false,
      };
    }
    case 'ADDED_TO_QUEUE': {
      const queue = state.queue;
      const queuedPairs = new Set(state.queue.map(routeParamsToString));
      const responses = new Set(
        state.responses.map((r) => routeParamsToString(r.params)),
      );
      const params = action.locationPairs.map(
        locationPairToRouteParams(state.locations),
      );
      const incomingPairs = new Set(params.map(routeParamsToString));

      // Don't requeue already fetched or queued distances
      const newPairs = params.filter((pair): pair is RouteParams => {
        if (!pair) {
          return false;
        }
        const [
          fromClusterId,
          toClusterId,
          avoidsChannels,
          draft,
          usedNoGoZones,
        ] = pair;

        const isFetched = state.distances.map.get(
          fromClusterId,
          toClusterId,
          avoidsChannels,
          draft ?? state.draft,
          usedNoGoZones ?? state.noGoZones,
        );
        const pairString = routeParamsToString(pair);
        const isQueued = queuedPairs.has(pairString);
        const hasResponse = responses.has(pairString);

        const willBeQueued = !isFetched && !isQueued && !hasResponse;
        if (willBeQueued) {
          queuedPairs.add(pairString);
        }

        return willBeQueued;
      });

      // Remove locationPair from errors if it's requeued
      const newErrors = state.errors.filter(({ params }) => {
        return !incomingPairs.has(routeParamsToString(params));
      });

      const newQueue = [...queue, ...newPairs];

      window.distancesLoaded = newQueue.length === 0;

      if (
        newPairs.length === 0 &&
        queue !== initialState.queue &&
        newErrors.length === state.errors.length
      ) {
        return state;
      }

      return {
        ...state,
        queue: newQueue,
        errors: newErrors,
      };
    }
    case 'RESPONSE_RECEIVED': {
      const searchKey = routeParamsToString(action.response.params);
      const newQueue = state.queue.filter(
        (queueItem) => routeParamsToString(queueItem) !== searchKey,
      );

      window.distancesLoaded = newQueue.length === 0;

      return {
        ...state,
        queue: newQueue,
        responses: [...state.responses, action.response],
      };
    }
    case 'RESPONSES_RECEIVED': {
      if (action.responses.length === 0) {
        return state;
      }
      const addedPairs = new Set(
        action.responses.map((d) => routeParamsToString(d.params)),
      );

      const newQueue = state.queue.filter(
        (queuedPair) => !addedPairs.has(routeParamsToString(queuedPair)),
      );

      window.distancesLoaded = newQueue.length === 0;

      return {
        ...state,
        queue: newQueue,
        responses: [...state.responses, ...action.responses],
      };
    }
    case 'ERROR_RECEIVED': {
      const searchKey = routeParamsToString(action.response.params);
      const newQueue = state.queue.filter(
        (queueItem) => routeParamsToString(queueItem) !== searchKey,
      );

      window.distancesLoaded = newQueue.length === 0;

      return {
        ...state,
        queue: newQueue,
        errors: [...state.errors, action.response],
      };
    }
    case 'LOADING_STARTED': {
      window.distancesLoaded = false;
      return {
        ...state,
        loading: true,
      };
    }
    case 'LOADING_ENDED': {
      window.distancesLoaded = true;
      return {
        ...state,
        loading: false,
      };
    }
    case 'UNAPPROVED_QUEUE_RESET': {
      return {
        ...state,
        unapprovedQueue: action.locationPairs,
      };
    }
    case 'SET_DRAFT': {
      return {
        ...state,
        distances:
          state.distances === initialDistanceMap
            ? initialDistanceMap
            : new DistanceMap(
                state.distances.map,
                state.locations,
                action.draft,
                state.noGoZones,
                state.distances.overrides,
              ),
        draft: action.draft,
      };
    }
    case 'SET_NOGOZONES': {
      return {
        ...state,
        distances:
          state.distances === initialDistanceMap
            ? initialDistanceMap
            : new DistanceMap(
                state.distances.map,
                state.locations,
                state.draft,
                action.noGoZones,
                state.distances.overrides,
              ),
        noGoZones: action.noGoZones,
      };
    }
    case 'SET_LOCATIONS': {
      return {
        ...state,
        distances:
          state.distances === initialDistanceMap
            ? initialDistanceMap
            : new DistanceMap(
                state.distances.map,
                action.locations,
                state.draft,
                state.noGoZones,
                state.distances.overrides,
              ),
        locations: action.locations,
      };
    }
    case 'SET_OVERRIDES': {
      return {
        ...state,
        distances:
          state.distances === initialDistanceMap
            ? initialDistanceMap
            : new DistanceMap(
                state.distances.map,
                state.locations,
                state.draft,
                state.noGoZones,
                action.overrides,
              ),
        overrides: action.overrides,
      };
    }
    case 'FETCHING_APPROVED': {
      const queuedPairs = new Set(state.queue.map(routeParamsToString));

      const newPairs = state.unapprovedQueue
        .map(locationPairToRouteParams(state.locations))
        .filter((pair): pair is RouteParams => {
          if (!pair) {
            return false;
          }
          const [
            fromClusterId,
            toClusterId,
            avoidsChannels,
            draft,
            usedNoGoZones,
          ] = pair;
          const isFetched = state.distances.map.get(
            fromClusterId,
            toClusterId,
            avoidsChannels,
            draft ?? state.draft,
            usedNoGoZones ?? state.noGoZones,
          );
          const pairString = routeParamsToString(pair);
          const isQueued = queuedPairs.has(pairString);

          const willBeQueued = !isFetched && !isQueued;
          if (willBeQueued) {
            queuedPairs.add(pairString);
          }

          return willBeQueued;
        });

      const newQueue = [...state.queue, ...newPairs];

      window.distancesLoaded = newQueue.length === 0;

      return {
        ...state,
        unapprovedQueue: [],
        queue: newQueue,
      };
    }
    default: {
      exhaustiveCheck(action);
    }
  }
};

/** Returns a string listing failed port pairs up to a maximum count. Adds "and x others" when the number of rows is growing too large. */
const summarizeErrors = (
  errors: DistanceError[],
  locations: Map<LocationId, ScenarioLocation>,
): string => {
  const rowCountByPortId = new Map<LocationId, number>();
  const rows: string[] = [];
  const maxRowsPerPort = 3;
  const maxTotalRows = 10;
  const locationIdsByClusterId = new DefaultMap<ClusterId, Set<LocationId>>(
    () => new Set(),
  );
  for (const [locationId, { clusterId }] of locations) {
    if (clusterId == null) continue;
    locationIdsByClusterId.get(clusterId).add(locationId);
  }
  main: for (const error of errors) {
    const [fromClusterId, toClusterId] = error.params;
    for (const fromId of locationIdsByClusterId.get(fromClusterId)) {
      for (const toId of locationIdsByClusterId.get(toClusterId)) {
        const fromPortRows = rowCountByPortId.get(fromId) ?? 0;
        const toPortRows = rowCountByPortId.get(toId) ?? 0;
        const existingRowCount = Math.max(fromPortRows, toPortRows);
        if (existingRowCount >= maxRowsPerPort) {
          continue;
        }
        rowCountByPortId.set(fromId, fromPortRows + 1);
        rowCountByPortId.set(toId, toPortRows + 1);
        const fromName = locations.get(fromId)?.name ?? `#${fromId}`;
        const toName = locations.get(toId)?.name ?? `#${toId}`;
        rows.push(`${fromName} - ${toName}`);
        if (rows.length >= maxTotalRows) {
          break main;
        }
      }
    }
  }
  if (rows.length < errors.length) {
    rows.push(`and ${errors.length - rows.length} others`);
  }
  return rows.join(', ');
};

const notifyFetchedToastId = 'DISTANCE_FETCHED';
const notifyMissingToastId = 'DISTANCE_MISSING';

declare global {
  interface Window {
    distancesLoaded: boolean;
  }
}

if (typeof window !== 'undefined') {
  window.distancesLoaded = false;
}

export const nonEmptyArrayOrNull = function <T extends ArrayLike<unknown>>(
  array: T | null,
) {
  return array?.length ? array : null;
};

export const parseNoGoZonesIds = (
  nodes: Array<{ id?: number } | null> | undefined,
) => {
  const ids = nonEmptyArrayOrNull(
    nodes
      ?.map((n) => n?.id)
      .filter(isDefined)
      .sort((a, b) => a - b) ?? null,
  );
  const str = String(ids);
  return [ids, str] as const;
};

export const DistanceProvider = ({
  children,
}: DistanceContextProviderProps) => {
  const client = useApolloClient();
  const currentUser = useCurrentUser();
  const organisationId = currentUser?.organizationId ?? null;
  const isLoggedIn = !!currentUser;

  const notifyFetched = useNotify({
    toastId: notifyFetchedToastId,
    dismissible: true,
  });
  const notifyFetching = useNotify({
    toastId: notifyMissingToastId,
    autoClose: false,
  });

  const allNoGoZones = useAllNoGoZonesQuery();
  const noGoZoneNodes = allNoGoZones.data?.allNoGoZones?.nodes;
  const [noGoZones, noGoZonesString] = useMemo(
    () => parseNoGoZonesIds(noGoZoneNodes),
    [noGoZoneNodes],
  );

  const [
    {
      distances,
      loading,
      queue,
      unapprovedQueue,
      responses,
      errors,
      draft,
      locations,
      overrides,
    },
    dispatch,
  ] = useReducer(distancesReducer, getInitialState());

  const setDraft = useCallback(
    (draft: number | null) => {
      dispatch({ type: 'SET_DRAFT', draft });
    },
    [dispatch],
  );

  const setNoGoZones = useCallback(
    (noGoZones: string) => {
      dispatch({ type: 'SET_NOGOZONES', noGoZones });
    },
    [dispatch],
  );

  const setLocations = useCallback(
    (locations: Map<LocationId, ScenarioLocation>) => {
      dispatch({ type: 'SET_LOCATIONS', locations });
    },
    [dispatch],
  );

  const storedOverrides = useRef<OverrideResponse[]>([]);
  const allRouteOverrides = useAllRouteOverridesQuery();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!isFirstRender.current) return;
    const overrides =
      allRouteOverrides.data?.allRouteOverrides?.nodes.filter(isDefined) ?? [];
    if (overrides.length) {
      const overrideResponses: OverrideResponse[] = [];

      for (const ro of overrides) {
        const { distance, avoidsChannels, fromId, toId } = ro;
        if (distance == null) return;

        overrideResponses.push({
          params: [fromId, toId, avoidsChannels],
          response: { dist: Number(distance), is_override: true },
        });
      }

      storedOverrides.current = overrideResponses;

      if (overrideResponses.length > 0) {
        dispatch({
          type: 'OVERRIDES_ADDED',
          distances: overrideResponses,
        });
      }

      isFirstRender.current = false;
    }
  }, [allRouteOverrides, draft, noGoZonesString]);

  const overrideSubscriptionOptions = useMemo(() => {
    return {
      variables: {
        org: `${organisationId}`,
      },
      skip: typeof window === 'undefined' || !organisationId,
      onData(options: OnDataOptions<RouteOverrideUpdatedSubscription>): void {
        const override =
          options.data?.data?.routeOverrideUpdated?.routeOverride;

        if (override) {
          const { distance, avoidsChannels, fromId, toId } = override;
          if (distance == null) return;
          dispatch({
            type: 'OVERRIDES_ADDED',
            distances: [
              {
                response: { dist: Number(distance), is_override: true },
                params: [fromId, toId, avoidsChannels],
              },
            ],
          });
        }
      },
    };
  }, [organisationId]);

  useRouteOverrideUpdatedSubscription(overrideSubscriptionOptions);

  // Initialize draft on login
  useEffect(() => {
    if (currentUser) {
      setDraft(stringToNumOpt(currentUser.rootOrganization?.draft) ?? null);
    }
  }, [currentUser, setDraft]);

  useEffect(() => {
    if (noGoZonesString) {
      setNoGoZones(noGoZonesString);
    }
  }, [setNoGoZones, noGoZonesString]);

  const distRef = useAsRef(distances);

  // Initialize distances from idb cache
  const isFirstLoadStoredDistances = useRef(true);
  useEffect(() => {
    if (!isFirstLoadStoredDistances.current) return;
    const asyncEffect = async () => {
      const storedDistances = await getStoredDistances(
        draft,
        locations,
        distRef,
        noGoZonesString,
      );

      // This prevents overrides from being cleared during reset
      if (storedOverrides.current.length > 0) {
        // Apply all overrides to the stored distances
        storedOverrides.current.forEach(({ response, params }) => {
          const [fromId, toId, avoidsChannels] = params;
          storedDistances.overrides.set(response, fromId, toId, avoidsChannels);
        });
      }

      dispatch({
        type: 'DISTANCES_RESET',
        distances: storedDistances,
      });
    };

    if (locations.size && isLoggedIn) {
      isFirstLoadStoredDistances.current = false;
      asyncEffect();
    }
  }, [draft, isLoggedIn, locations, distRef, noGoZonesString]);

  // Process queue
  useEffect(() => {
    if (
      !isLoggedIn ||
      organisationId === null ||
      queue.length === 0 ||
      loading ||
      locations.size === 0
    ) {
      return;
    }

    const asyncEffect = async () => {
      dispatch({ type: 'LOADING_STARTED' });
      await fetchDistancesInChunks(queue, draft, noGoZones, client, {
        onResponse: (_remaining, responses) => {
          dispatch({ type: 'RESPONSES_RECEIVED', responses });
        },
        onError: (response) => {
          dispatch({ type: 'ERROR_RECEIVED', response });
        },
      });

      dispatch({ type: 'LOADING_ENDED' });
    };

    asyncEffect();
  }, [
    client,
    draft,
    isLoggedIn,
    organisationId,
    loading,
    queue,
    locations.size,
    noGoZones,
    locations,
  ]);

  // Update toast during loading and add new distances once whole queue is processed
  useEffect(() => {
    const loading = queue.length > 0;
    const ready = queue.length == 0 && queue !== initialState.queue;
    window.distancesLoaded = !loading;

    if (loading) {
      let message = `Loading distances, ${queue.length} remaining`;
      if (errors.length > 0) {
        message +=
          `, ${errors.length} errors:\n` + summarizeErrors(errors, locations);
      }

      notifyFetched(message, { type: 'info', isLoading: true });
    } else if (ready) {
      dispatch({ type: 'QUEUE_PROCESSED' });
      if (errors.length > 0) {
        notifyFetched(
          `Failed to load ${errors.length} distances: ` +
            summarizeErrors(errors, locations),
          {
            type: 'error',
          },
        );
      } else {
        notifyFetched('Distances loaded', {
          type: 'success',
          updateOnly: true,
          secondaryId: `${notifyFetchedToastId}-${responses.length}`,
          isLoading: false,
        });
      }
    }
  }, [notifyFetched, responses, queue, errors, locations]);

  const storedDistancesLoaded = distances !== initialDistanceMap;

  const queueDistancesForLocationPairs = useCallback(
    (locationPairs: LocationPair[]) => {
      if (locationPairs.length && storedDistancesLoaded) {
        dispatch({ type: 'ADDED_TO_QUEUE', locationPairs });
      }
    },
    [storedDistancesLoaded],
  );

  const resetDistances = useCallback(() => {
    dispatch({
      type: 'DISTANCES_RESET',
      distances: new DistanceMap(
        new MultiKey(5),
        locations,
        draft,
        noGoZonesString,
        overrides,
      ),
    });
  }, [locations, draft, noGoZonesString, overrides]);

  const queueAllDistancesBetweenLocations = useCallback(
    (locationIds: Set<LocationId>) => {
      const locationPairs = getAllResolvedLocationPairs(
        locationIds,
        noGoZonesString,
        locations,
      );

      dispatch({ type: 'UNAPPROVED_QUEUE_RESET', locationPairs });
    },
    [noGoZonesString, locations],
  );

  const fetchMissingDistances = useCallback(
    async (
      existingDistances: Distances,
      locationIds: Set<LocationId>,
      includeAvoidsChannels = false,
    ): Promise<Distances> => {
      if (!isLoggedIn || organisationId === null || !storedDistancesLoaded) {
        return Promise.reject();
      }

      const missingDistances = getMissingDistances(
        existingDistances,
        locationIds,
        noGoZonesString,
        locations,
        includeAvoidsChannels,
      ).filter(isDefined);

      if (missingDistances.length === 0) {
        return existingDistances;
      }

      const notifyProgress = (remaining: number) => {
        notifyFetching(`Fetching required distances, ${remaining} remaining`, {
          type: 'info',
        });
      };

      const notifyFetchError = (error: unknown) => {
        console.error('Error fetching missing distances', error);
        notifyFetching(`Error fetching required distances`, {
          type: 'error',
        });
      };

      notifyProgress(missingDistances.length);

      const fetchedDistances = await fetchDistancesInChunks(
        missingDistances,
        draft,
        noGoZones,
        client,
        {
          onResponse: notifyProgress,
          onError: notifyFetchError,
        },
        { storeToIndexedDb: true },
      );

      const resultDistanceMap = new DistanceMap(
        existingDistances.map,
        locations,
        draft,
        noGoZonesString,
        overrides,
      );
      for (const { params, response } of fetchedDistances) {
        const [
          fromClusterId,
          toClusterId,
          avoidsChannels,
          draft,
          usedNoGoZones,
        ] = params;
        resultDistanceMap.set(
          response,
          fromClusterId,
          toClusterId,
          avoidsChannels,
          draft,
          usedNoGoZones,
        );
      }

      notifyFetching(`Fetching required distances done`, {
        type: 'success',
        autoClose: 5,
      });

      // Update store
      dispatch({
        type: 'DISTANCES_ADDED',
        distances: fetchedDistances,
      });

      return resultDistanceMap;
    },
    [
      isLoggedIn,
      organisationId,
      storedDistancesLoaded,
      noGoZonesString,
      locations,
      draft,
      noGoZones,
      client,
      overrides,
      notifyFetching,
    ],
  );

  const contextValue = useMemo<DistanceContextProps>(
    () => ({
      dispatch,
      distances,
      noGoZones: noGoZonesString,
      queueDistancesForLocationPairs,
      queueAllDistancesBetweenLocations,
      fetchMissingDistances,
      setLocations,
      resetDistances,
    }),
    [
      distances,
      noGoZonesString,
      queueDistancesForLocationPairs,
      queueAllDistancesBetweenLocations,
      fetchMissingDistances,
      setLocations,
      resetDistances,
    ],
  );

  return (
    <DistanceContext.Provider value={contextValue}>
      {unapprovedQueue.length > 0 ? (
        <GetAllDistancesModal dispatch={dispatch} />
      ) : null}
      {children}
    </DistanceContext.Provider>
  );
};

const GetAllDistancesModal = ({
  dispatch,
}: {
  dispatch: DistancesDispatch;
}) => {
  const onApproveFetching = () => {
    dispatch({ type: 'FETCHING_APPROVED' });
  };

  const onClearUnapproved = () => {
    dispatch({ type: 'UNAPPROVED_QUEUE_RESET', locationPairs: [] });
  };
  const isMobileView = useIsMobileView();

  return (
    <Modal
      isOpen={true}
      onClose={onClearUnapproved}
      width={isMobileView ? '350px' : '568px'}
      height="260px"
    >
      <ModalBody>
        <Text>
          Missing distances need to be preloaded before performing automatic
          scheduling. This may take a while.
        </Text>
      </ModalBody>
      <ModalFooter>
        <Button onPress={onClearUnapproved} mr={3}>
          Cancel
        </Button>
        <Button variant="positive" onPress={onApproveFetching}>
          Fetch missing distances
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const fetchDistance = async (
  params: RouteParams,
  draft: number | null,
  noGoZones: number[] | null,
  client: ApolloClient<unknown>,
  { storeToIndexedDb = true }: { storeToIndexedDb?: boolean } = {},
): Promise<RouteData> => {
  const { data } = await client.query<
    GetClusterRouteQueryResult['data'],
    GetClusterRouteQueryVariables
  >({
    query: GetClusterRouteDocument,
    variables: {
      avoidsChannels: params[2],
      draft,
      fromClusterId: params[0],
      toClusterId: params[1],
      noGoZones,
    },
    fetchPolicy: 'no-cache',
  });

  if (!data) {
    throw new Error('Response data missing');
  }

  const response = parseRoute(data);

  if (storeToIndexedDb) {
    // Store only after parsing response so that we don't store responses that fail to parse
    setStoredDistance(params, draft, data, String(noGoZones));
  }

  return response;
};

async function fetchDistances(
  allPairs: RouteParams[],
  draft: number | null,
  onlyCached: boolean,
  noGoZones: number[] | null,
  client: ApolloClient<unknown>,
  { storeToIndexedDb = true }: { storeToIndexedDb?: boolean } = {},
): Promise<[RouteParams, RouteData | null][]> {
  const [validPairs, invalidPairs] = partition(allPairs, ([from, to]) => {
    return from && to;
  });

  const inputs = validPairs.map(([...pair]) => ({
    avoidsChannels: pair[2],
    draft,
    noGoZones,
    fromClusterId: pair[0],
    toClusterId: pair[1],
  }));

  const data = (
    await client.query<
      | GetCachedExactClusterRoutesQueryResult['data']
      | GetClusterRoutesQueryResult['data'],
      GetClusterRoutesQueryVariables
    >({
      query: onlyCached
        ? GetCachedExactClusterRoutesDocument
        : GetClusterRoutesDocument,
      variables: {
        inputs,
      },
      fetchPolicy: 'no-cache',
    })
  ).data;

  if (!data) {
    throw new Error('Response data missing');
  }

  const routes = new MultiKey<
    string,
    [number, number, boolean | null | undefined]
  >(3);

  const result =
    'cachedExactRoutesByClusterPairs' in data
      ? data.cachedExactRoutesByClusterPairs
      : 'routesByClusterPairs' in data
        ? data.routesByClusterPairs
        : null;
  result?.forEach(
    (
      route: Pick<
        ClusterRoute,
        'draft' | 'avoidsChannels' | 'exact' | 'fromClusterId' | 'toClusterId'
      > | null,
    ) => {
      if (!route || !route.exact) return;
      if (route.fromClusterId && route.toClusterId) {
        routes.set(
          route.exact,
          route.fromClusterId,
          route.toClusterId,
          route.avoidsChannels,
        );
      }
    },
  );

  const noGoZonesString = String(noGoZones);

  return validPairs
    .map((params): [RouteParams, RouteData | null] => {
      const [from, to, avoidsChannels] = params;
      try {
        const route = routes.get(from, to, avoidsChannels);
        if (!route) {
          return [params, null];
        }
        const data: GetClusterRouteQuery = {
          routeByClusterPair: route,
        };

        const response = parseRoute(data);

        if (storeToIndexedDb) {
          setStoredDistance(params, draft, data, noGoZonesString);
        }

        return [params, response];
      } catch (_e) {
        return [params, null];
      }
    })
    .concat(invalidPairs.map((pair) => [pair, null]));
}

export async function fetchDistancesInChunks(
  queue: RouteParams[],
  draft: number | null,
  noGoZones: number[] | null,
  client: ApolloClient<unknown>,
  callbacks: {
    onResponse?: (remaining: number, responses: DistanceResponse[]) => void;
    onError: (response: { params: RouteParams; error: string }) => void;
  },
  { storeToIndexedDb = true }: { storeToIndexedDb?: boolean } = {},
): Promise<DistanceResponse[]> {
  const remainingQueue = new Set(queue);
  const result: DistanceResponse[] = [];

  // Try cached distances first, then uncached
  for (const onlyCached of [true, false]) {
    if (!onlyCached && remainingQueue.size <= 15) break; // When just a few left, fetch them individually (enables us to update the toast)
    for (const chunkPairs of chunkArray(
      Array.from(remainingQueue),
      CACHED_DISTANCE_CHUNK_SIZE,
    )) {
      const results = await fetchDistances(
        chunkPairs,
        draft,
        onlyCached,
        noGoZones,
        client,
        { storeToIndexedDb },
      );

      const distances = filteredMap(results, ([params, response]) =>
        response ? { params, response } : NONE,
      );

      for (const distance of distances) {
        remainingQueue.delete(distance.params);
      }
      result.push(...distances);
      callbacks.onResponse?.(remainingQueue.size, distances);
    }
  }

  const fetchSinglePairTask = (params: RouteParams) => async () => {
    remainingQueue.delete(params);
    try {
      const response = await fetchDistance(params, draft, noGoZones, client, {
        storeToIndexedDb,
      });
      result.push({ params, response });
      callbacks.onResponse?.(remainingQueue.size, [
        {
          params: params,
          response,
        },
      ]);
    } catch (err) {
      callbacks.onError({
        params: params,
        error: err.message,
      });
    }
  };

  // Fetch remaining distances individually
  await Throttle.all(Array.from(remainingQueue).map(fetchSinglePairTask), {
    maxInProgress: 1,
  });

  return result;
}
